import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Bell, Newspaper, FolderTree, Layers, Megaphone, Users, Car,
  Settings, LogOut, Gauge, Menu, ChevronDown, ChevronRight, FileText, Briefcase,
  BookOpen, GraduationCap, Target, ClipboardCheck, Sparkles, FileSpreadsheet,
  School, UserCheck, Award, TrendingUp, Landmark, FileCheck, GitBranch, HelpCircle,
  User, KeyRound
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../utils/constants';

const C = COLORS;

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/notifications', label: 'Notifications', icon: Bell },
  {
    label: 'News Management',
    icon: Newspaper,
    children: [
      { path: '/news', label: 'News', icon: Newspaper },
      { path: '/main-categories', label: 'Main Category', icon: Layers },
      { path: '/categories', label: 'Sub Category', icon: FolderTree }
    ]
  },
  { path: '/ads', label: 'Ads', icon: Megaphone },
  { path: '/users', label: 'Users', icon: Users },
  { path: '/garage', label: 'Garage', icon: Car },
  {
    label: 'The Advansity',
    icon: GraduationCap,
    children: [
      { path: '/advansity-dashboard', label: 'Dashboard', icon: Gauge },
      {
        label: 'PAL',
        icon: BookOpen,
        children: [
          { path: '/pal/practice', label: 'Practice', icon: Target },
          { path: '/pal/tests', label: 'Tests', icon: ClipboardCheck },
          { path: '/pal/learning', label: 'Learning', icon: Sparkles },
          { path: '/pal/homework', label: 'Homework', icon: FileSpreadsheet }
        ]
      },
      {
        label: 'Management',
        icon: Briefcase,
        children: [
          { path: '/schools', label: 'Schools', icon: School },
          { path: '/teachers', label: 'Teachers', icon: UserCheck },
          { path: '/students', label: 'Students', icon: Users }
        ]
      },
      {
        label: 'Results',
        icon: FileText,
        children: [
          { path: '/results/tests', label: 'Tests', icon: Award },
          { path: '/results/practice', label: 'Practice', icon: TrendingUp }
        ]
      },
      {
        label: 'Admin',
        icon: Settings,
        children: [
          { path: '/config/board', label: 'Board', icon: Landmark },
          { path: '/config/exam', label: 'Exam', icon: FileCheck },
          { path: '/config/class', label: 'Class', icon: GraduationCap },
          { path: '/config/subject', label: 'Subjects', icon: BookOpen },
          { path: '/config/topic', label: 'Topic', icon: FolderTree },
          { path: '/config/sub-topic', label: 'Sub Topic', icon: GitBranch },
          { path: '/config/questions', label: 'Questions', icon: HelpCircle }
        ]
      }
    ]
  }
];

const getMenuItems = (role) => {
  if (role === 'admin') return menuItems;
  if (role === 'manager') {
    return menuItems.map(item => {
      if ((item.label === 'Dashboard' && item.path === '/dashboard') || item.label === 'Users') return item;
      if (item.label === 'The Advansity') {
        return {
          ...item,
          children: item.children.map(child => {
            if (child.path === '/advansity-dashboard' || ['Dashboard', 'PAL', 'Management', 'Results'].includes(child.label)) {
              return child;
            }
            if (child.label === 'Admin') {
              return {
                ...child,
                children: child.children.filter(subChild => ['Topic', 'Sub Topic', 'Questions'].includes(subChild.label))
              };
            }
            return null;
          }).filter(Boolean)
        };
      }
      return null;
    }).filter(Boolean);
  }
  return [];
};

const isChildActive = (childPath, currentPath) => {
  if (!childPath) return false;
  if (currentPath === childPath) return true;
  if (currentPath.startsWith(childPath + '/')) return true;
  if (childPath === '/schools' && currentPath.startsWith('/school')) return true;
  if (childPath === '/teachers' && currentPath.startsWith('/teacher')) return true;
  if (childPath === '/students' && currentPath.startsWith('/student')) return true;
  return false;
};

const hasActiveChild = (item, currentPath) => {
  if (item.path && isChildActive(item.path, currentPath)) return true;
  if (item.children) {
    return item.children.some(child => hasActiveChild(child, currentPath));
  }
  return false;
};

const SubMenuItem = ({ item, currentPath }) => {
  const isSubActive = hasActiveChild(item, currentPath);
  const [subOpen, setSubOpen] = useState(isSubActive);

  useEffect(() => {
    if (isSubActive) {
      setSubOpen(true);
    }
  }, [isSubActive]);

  if (item.children) {
    return (
      <div style={{ marginBottom: 2 }}>
        <div
          onClick={() => setSubOpen(!subOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '7px 10px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: isSubActive ? 600 : 500,
            color: isSubActive ? C.primary : C.g700,
            background: isSubActive ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {item.icon && <item.icon size={16} />}
            <span>{item.label}</span>
          </div>
          {subOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
        {subOpen && (
          <div style={{ paddingLeft: 10, marginTop: 2, borderLeft: `1.5px solid ${C.g200}`, marginLeft: 16 }}>
            {item.children.map(subChild => {
              const childActive = isChildActive(subChild.path, currentPath);
              return (
                <NavLink
                  key={subChild.path}
                  to={subChild.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    borderRadius: 6,
                    fontSize: 12.5,
                    fontWeight: childActive ? 600 : 400,
                    color: childActive ? C.primary : C.g500,
                    background: childActive ? '#F3F4F6' : 'transparent',
                    textDecoration: 'none',
                    marginBottom: 2,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {subChild.icon && <subChild.icon size={13} style={{ opacity: childActive ? 1 : 0.65, flexShrink: 0 }} />}
                  <span>{subChild.label}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Direct link inside parent
  const isDirectActive = isChildActive(item.path, currentPath);
  return (
    <NavLink
      to={item.path}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 10px',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: isDirectActive ? 600 : 500,
        color: isDirectActive ? C.primary : C.g700,
        background: isDirectActive ? 'rgba(79, 70, 229, 0.08)' : 'transparent',
        textDecoration: 'none',
        marginBottom: 2,
        transition: 'all 0.15s ease',
      }}
    >
      {item.icon && <item.icon size={16} />}
      <span>{item.label}</span>
    </NavLink>
  );
};

const MenuItem = ({ item, sidebarOpen }) => {
  const location = useLocation();
  const isActive = hasActiveChild(item, location.pathname);
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setOpen(true);
    }
  }, [isActive]);

  if (item.children) {
    return (
      <div style={{ marginBottom: 2 }}>
        <div 
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: sidebarOpen ? '11px 14px' : '11px 0',
            justifyContent: sidebarOpen ? 'space-between' : 'center', borderRadius: 10, fontSize: 14,
            fontWeight: isActive ? 600 : 400, color: isActive ? C.primary : C.g600,
            background: isActive ? C.primaryGhost : 'transparent', cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <item.icon size={20} />
            {sidebarOpen && <span>{item.label}</span>}
          </div>
          {sidebarOpen && (open ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </div>
        {sidebarOpen && open && (
          <div style={{ paddingLeft: 18, marginTop: 4 }}>
            {item.children.map((child, idx) => (
              <SubMenuItem key={child.path || child.label || idx} item={child} currentPath={location.pathname} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink to={item.path} style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: sidebarOpen ? '11px 14px' : '11px 0',
      justifyContent: sidebarOpen ? 'flex-start' : 'center', borderRadius: 10, fontSize: 14,
      fontWeight: isActive ? 600 : 400, color: isActive ? C.primary : C.g600,
      background: isActive ? C.primaryGhost : 'transparent', marginBottom: 2, textDecoration: 'none',
    })}>
      <item.icon size={20} />
      {sidebarOpen && <span>{item.label}</span>}
    </NavLink>
  );
};

const AppLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : 'U';

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.g50 }}>
      {/* Sidebar */}
      <aside style={{ width: sidebarOpen ? 256 : 72, background: '#fff', borderRight: `1px solid ${C.g200}`, display: 'flex', flexDirection: 'column', transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)', flexShrink: 0, overflowX: 'hidden', overflowY: 'auto', zIndex: 100 }}>
        <div style={{ padding: sidebarOpen ? '20px 20px 16px' : '20px 12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${C.g100}`, minHeight: 68 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 12px rgba(79,70,229,0.25)' }}>
            <Gauge size={22} color="#fff" />
          </div>
          {sidebarOpen && <span style={{ fontSize: 18, fontWeight: 800, color: C.g900, letterSpacing: -0.3, whiteSpace: 'nowrap' }}>Worldeye</span>}
        </div>

        <nav style={{ flex: 1, padding: '12px 8px' }}>
          {sidebarOpen && <p style={{ margin: '0 12px 8px', fontSize: 11, fontWeight: 600, color: C.g400, textTransform: 'uppercase', letterSpacing: 1 }}>Menu</p>}
          {getMenuItems(user?.role).map((item, idx) => (
            <MenuItem key={item.path || idx} item={item} sidebarOpen={sidebarOpen} />
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <header style={{ background: '#fff', borderBottom: `1px solid ${C.g200}`, padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: C.g500 }}><Menu size={22} /></button>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.g900 }}>{title}</h2>
          </div>

          {/* User Profile Dropdown */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 10px',
                borderRadius: 12,
                cursor: 'pointer',
                background: userMenuOpen ? C.g100 : 'transparent',
                transition: 'background 0.15s ease',
                userSelect: 'none',
              }}
              onMouseEnter={(e) => { if (!userMenuOpen) e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={(e) => { if (!userMenuOpen) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                boxShadow: '0 2px 8px rgba(79,70,229,0.2)',
                flexShrink: 0
              }}>
                {initials}
              </div>
              <div style={{ textAlign: 'left' }}>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: C.g900, lineHeight: 1.2 }}>
                  {user?.first_name} {user?.last_name}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: C.g400, textTransform: 'capitalize' }}>
                  {user?.role}
                </p>
              </div>
              <ChevronDown
                size={15}
                color={C.g500}
                style={{
                  transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  marginLeft: 2,
                }}
              />
            </div>

            {/* Dropdown Menu Popup */}
            {userMenuOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: 230,
                background: '#fff',
                borderRadius: 14,
                boxShadow: '0 12px 30px -4px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
                border: `1px solid ${C.g200}`,
                padding: '6px',
                zIndex: 1000,
              }}>
                {/* User Info Header inside dropdown */}
                <div style={{ padding: '10px 12px 8px', borderBottom: `1px solid ${C.g100}`, marginBottom: 4 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.g900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11.5, color: C.g500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.email || 'Logged in user'}
                  </p>
                </div>

                {/* Option: Profile */}
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'transparent',
                    color: C.g700,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.g50; e.currentTarget.style.color = C.primary; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.g700; }}
                >
                  <User size={16} />
                  <span>Profile</span>
                </button>

                {/* Option: Settings */}
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'transparent',
                    color: C.g700,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.g50; e.currentTarget.style.color = C.primary; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.g700; }}
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </button>

                {/* Option: Change Password */}
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); navigate('/change-password'); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'transparent',
                    color: C.g700,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.g50; e.currentTarget.style.color = C.primary; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.g700; }}
                >
                  <KeyRound size={16} />
                  <span>Change Password</span>
                </button>

                {/* Divider */}
                <div style={{ height: 1, background: C.g100, margin: '4px 6px' }} />

                {/* Option: Logout */}
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'transparent',
                    color: C.danger,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <LogOut size={16} color={C.danger} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </header>

        <main style={{ flex: 1, padding: 28, overflowY: 'auto' }}>{children}</main>
      </div>
    </div>
  );
};

export default AppLayout;
